import { createContext, useContext, useEffect, useState } from 'react';
import { useEventCallback } from '../hooks/useEventCallback';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * Creates a general-purpose app provider for managing any number of values in an app.
 * This can be used for stores, services, or any other values that need to be shared
 * across components in a React application.
 */
export const createAppProvider = () => {
  const AppRegistryContext = /*#__PURE__*/createContext(null);
  const AppProvider = ({
    children,
    setup,
    teardown
  }) => {
    const [registry] = useState(() => setup());
    const stableTeardown = useEventCallback(value => {
      teardown === null || teardown === void 0 || teardown(value);
    });
    useEffect(() => {
      return () => {
        stableTeardown(registry);
      };
    }, [registry, stableTeardown]);
    return /*#__PURE__*/_jsx(AppRegistryContext.Provider, {
      value: registry,
      children: children
    });
  };
  const useAppValue = key => {
    const registry = useContext(AppRegistryContext);
    if (!registry) {
      throw new Error('useAppValue must be used within an AppProvider');
    }
    const store = registry[key];
    if (!(key in registry) || store === undefined) {
      throw new Error(`Value with key "${key.toString()}" not found`);
    }
    return store;
  };
  return {
    AppProvider,
    useAppValue
  };
};